import React, { useEffect, useRef } from 'react';
import { styles } from './ProductHtmlBanner.css';
import { ProductBannerProduct, ProductHtmlBannerProps } from './types';
import { ProductBannerHeader } from './ProductBannerHeader';
import { ProductBannerCard } from './ProductBannerCard';
import { formatDate } from './utils';
import { useResponsiveValue } from '../hooks/useResponsiveValue';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { useIsOnScreen } from '../hooks/useIsOnScreen/useIsOnScreen';
import { useTrackingContext } from '../Tracking';
import { trackingDataToDataAttributes } from '../HeroBanner/utils';
import { TermsAndConditionsLink } from '../TermsAndConditions/TermsAndConditionsLink';

type ProductBannerVariant = 'mobile' | 'small' | 'medium' | 'large';

export const ProductHtmlBanner = ({
  heading,
  url,
  overallEndDate,
  headingTheme,
  contentProducts: products,
  trackingData,
  termsAndConditions,
}: ProductHtmlBannerProps) => {
  const filteredProducts = products.filter(product => !!product && product.hit) as Array<
    ProductBannerProduct & { hit: AlgoliaProductHit }
  >;

  const productCount = filteredProducts?.length || 0;

  const { cardCount, cardVariant } = useResponsiveValue<{ cardCount: number; cardVariant: ProductBannerVariant }>({
    xs: { cardCount: 1, cardVariant: 'mobile' },
    sm: { cardCount: 1, cardVariant: 'medium' },
    lg: { cardCount: 2, cardVariant: productCount === 1 ? 'large' : 'medium' },
    xl: {
      cardCount: productCount,
      cardVariant: productCount === 1 ? 'large' : productCount === 2 ? 'medium' : 'small',
    },
  });

  const showOverallEndDate = filteredProducts.every(product => !product.endDate);

  const containerRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useIsOnScreen(containerRef, { persistOnceSeen: true });
  const { tracker } = useTrackingContext();

  const haveTrackedImpression = useRef<boolean>(false);
  useEffect(() => {
    if (isOnScreen && !haveTrackedImpression.current) {
      haveTrackedImpression.current = true;
      tracker.trackCustomEvent({ event: 'internal promotion impression', gaipElement: containerRef.current });
    }
  }, [isOnScreen, tracker]);

  return (
    <div
      ref={containerRef}
      className={styles.containerDefault}
      {...trackingDataToDataAttributes(
        trackingData ? { ...trackingData, category: trackingData.category || tracker.category } : undefined
      )}
    >
      {heading && url ? (
        <ProductBannerHeader
          heading={heading}
          url={url}
          overallEndDate={showOverallEndDate && cardVariant !== 'large' ? formatDate(overallEndDate, true) : undefined}
          theme={headingTheme}
        />
      ) : null}
      <div className={styles.productBannerCardsContainerWrapper}>
        <div className={styles.productBannerCardsContainerInnerWrapper}>
          <div className={styles.productBannerCardsContainer({ cardVariant })}>
            {filteredProducts?.splice(0, cardCount).map(product => {
              const date = showOverallEndDate && cardVariant === 'large' ? overallEndDate : product.endDate;
              const shouldHideYear =
                cardVariant === 'large' || (cardVariant === 'mobile' && product.tagType === 'Badge');
              return (
                <ProductBannerCard
                  variant={cardVariant}
                  key={product.hit.product_id}
                  hit={product.hit}
                  endDate={formatDate(date, shouldHideYear)}
                  badge={product.badge}
                  badgeStyle={product.badgeStyle}
                  enableEAPriceBox={product.enableEAPriceBox}
                  tagType={product.tagType}
                  trackingData={trackingData}
                />
              );
            })}
          </div>

          {termsAndConditions ? (
            <div
              className={styles.termsLinkContainer({
                isWhite: termsAndConditions?.linkColor === 'white',
                isMoreThenOnecard: cardCount > 1,
              })}
            >
              <TermsAndConditionsLink termsAndConditions={termsAndConditions} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
